import { DragEvent, ReactNode, useEffect, useMemo, useState } from "react"
import { ActionIcon, Badge, Box, Button, Card, Checkbox, Group, LoadingOverlay, Menu, Paper, ScrollArea, SegmentedControl, Select, Stack, Text, Tooltip } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { IconCalendarEvent, IconCheck, IconChevronLeft, IconChevronRight, IconUserPlus, IconX } from "@tabler/icons-react"
import { openConfirmModal } from "@mantine/modals"
import dayjs from "dayjs"
import { headerStore } from "@shared/state/header.state.ts"
import { useLogisticsPermissions } from "../hooks/use-logistics-permissions"
import { useAssignableRiders } from "@features/rider/hooks/use-riders"
import { useAssignStops, useConfirmarRecojo, useDailySchedule } from "../hooks/use-delivery-stops"
import { DailyScheduleRider, DeliveryStop, isPickupToConfirm, ODOO_STATE_LABEL, STOP_ACTION, STOP_ACTION_LABEL, STOP_STATUS, STOP_STATUS_LABEL } from "../domain/delivery-stop.types"
import { commonCountryOf, esPuntoEcuador, ridersForStops } from "../utils/logistics-country"
import { useCountryScope } from "@/shared/country/use-country-scope"
import { ALL_COUNTRIES, COUNTRY_SELECTION_OPTIONS, countryQueryParam } from "@/shared/country/country-scope"
import { CountryBadge } from "@/shared/country/country-components"

type TypeFilter = "all" | typeof STOP_ACTION.DELIVERY | typeof STOP_ACTION.PICKUP

const ACTION_COLOR = { [STOP_ACTION.DELIVERY]: "orange", [STOP_ACTION.PICKUP]: "cyan" } as const
const COLUMN_WIDTH = 310
const DRAG_TYPE = "application/x-biocells-stop-ids"

const timeOf = (stop: DeliveryStop): number => (stop.scheduledDate ? new Date(stop.scheduledDate).getTime() : 0)
const byTime = (a: DeliveryStop, b: DeliveryStop): number => timeOf(a) - timeOf(b) || (a.suggestedOrder ?? 0) - (b.suggestedOrder ?? 0)

/** Solo lo pendiente o confirmado se puede mover: lo que ya salió o terminó no se reasigna */
const isMovable = (stop: DeliveryStop): boolean => stop.status === STOP_STATUS.PENDING || stop.status === STOP_STATUS.CONFIRMED

const riderNameOf = (rider: DeliveryStop["rider"]): string | undefined => (rider && typeof rider !== "string" ? rider.name : undefined)

type RiderOption = { id: string; name: string; country?: string }

interface StopCardProps {
  stop: DeliveryStop
  canManage: boolean
  canConfirm: boolean
  selected: boolean
  riders: RiderOption[]
  onToggle: (id: string) => void
  onAssign: (stopIds: string[], riderId: string) => void
  onConfirmPickup: (stop: DeliveryStop) => void
  onDragStart: (event: DragEvent<HTMLDivElement>, stop: DeliveryStop) => void
  showFormerRider: boolean
}

function StopCard({ stop, canManage, canConfirm, selected, riders, onToggle, onAssign, onConfirmPickup, onDragStart, showFormerRider }: StopCardProps) {
  const action = stop.action ?? STOP_ACTION.DELIVERY
  const scheduled = stop.scheduledDate
  const toConfirm = isPickupToConfirm(stop)
  const movable = isMovable(stop)
  const patients = [...new Set((stop.items ?? []).map((item) => item.patientName).filter(Boolean))]
  const sos = [...new Set((stop.items ?? []).map((item) => item.soName).filter(Boolean))]
  const emergency = (stop.items ?? []).some((item) => item.isEmergency)
  const hasRider = !!stop.rider
  const formerRider = showFormerRider ? riderNameOf(stop.rider) : undefined
  const dimmed = stop.status === STOP_STATUS.COMPLETED

  return (
    <Card
      withBorder
      radius="md"
      p="xs"
      draggable={canManage && movable}
      onDragStart={(event) => onDragStart(event, stop)}
      style={{
        borderLeft: `4px solid var(--mantine-color-${ACTION_COLOR[action]}-6)`,
        opacity: dimmed ? 0.6 : 1,
        cursor: canManage && movable ? "grab" : "default",
        outline: selected ? "2px solid var(--mantine-color-blue-5)" : undefined
      }}
    >
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap={6} wrap="wrap">
            <Badge color={ACTION_COLOR[action]} variant="filled" size="sm">
              {STOP_ACTION_LABEL[action]}
            </Badge>
            {scheduled && (
              <Text fz={12} fw={600}>
                {dayjs(scheduled).format("HH:mm")}
              </Text>
            )}
            {emergency && (
              <Badge color="red" variant="filled" size="xs">
                Urgente
              </Badge>
            )}
          </Group>
          {canManage && movable && <Checkbox size="xs" checked={selected} onChange={() => onToggle(stop.id)} aria-label="Seleccionar punto" />}
        </Group>

        <Text fz={12} fw={600} c="indigo">
          {stop.correlativo ?? "—"}
        </Text>
        <Text fz={12} lineClamp={2}>
          {stop.address || "Sin dirección"}
        </Text>
        {(patients.length > 0 || sos.length > 0) && (
          <Text fz={11} c="dimmed" lineClamp={2}>
            {[patients.join(", "), sos.join(", ")].filter(Boolean).join(" · ")}
          </Text>
        )}

        <Group gap={6} wrap="wrap">
          {toConfirm ? (
            <Tooltip label="Asignado, pero todavía no es recojo programado: confírmalo para sumarlo a la programación del despachador" multiline w={240} withArrow>
              <Badge color="pink" variant="filled" size="sm">
                Por confirmar
              </Badge>
            </Tooltip>
          ) : (
            <Badge variant="light" size="sm" color={stop.status === STOP_STATUS.COMPLETED ? "green" : stop.status === STOP_STATUS.CONFIRMED ? "teal" : stop.status === STOP_STATUS.IN_TRANSIT ? "blue" : "yellow"}>
              {stop.status ? STOP_STATUS_LABEL[stop.status] : "—"}
            </Badge>
          )}
          {stop.odooState && !toConfirm && (
            <Badge variant="outline" size="xs" color="gray">
              {ODOO_STATE_LABEL[stop.odooState] ?? stop.odooState}
            </Badge>
          )}
          {formerRider && (
            <Tooltip label="Este punto sigue asignado a alguien que ya no es asignable: reasígnalo" withArrow multiline w={220}>
              <Badge variant="light" color="gray" size="xs">
                Antes: {formerRider}
              </Badge>
            </Tooltip>
          )}
        </Group>

        {((canManage && movable) || (canConfirm && toConfirm)) && (
          <Group justify="space-between" gap={6} wrap="nowrap">
            {toConfirm && canConfirm && hasRider ? (
              <Button size="compact-xs" color="teal" leftSection={<IconCheck size={13} />} onClick={() => onConfirmPickup(stop)}>
                Confirmar recojo
              </Button>
            ) : toConfirm && canConfirm ? (
              <Text fz={11} c="dimmed">
                Asigna un despachador para poder confirmarlo
              </Text>
            ) : (
              <span />
            )}
            {canManage && movable && (
              <Menu withinPortal position="bottom-end" shadow="md">
                <Menu.Target>
                  <Tooltip label="Asignar a un despachador" withArrow>
                    <ActionIcon size="sm" variant="subtle" color="blue" aria-label="Asignar a un despachador">
                      <IconUserPlus size={15} />
                    </ActionIcon>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Asignar a</Menu.Label>
                  {ridersForStops(riders, [stop]).map((rider) => (
                    <Menu.Item key={rider.id} onClick={() => onAssign([stop.id], rider.id)}>
                      {rider.name}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        )}
      </Stack>
    </Card>
  )
}

interface ColumnProps {
  title: string
  subtitle?: ReactNode
  stops: DeliveryStop[]
  riderId?: string
  dropEnabled: boolean
  dragOver: boolean
  onDragOver: (riderId: string | null) => void
  onDrop: (event: DragEvent<HTMLDivElement>, riderId: string) => void
  emptyText: string
  render: (stop: DeliveryStop) => ReactNode
  accent?: string
}

function Column({ title, subtitle, stops, riderId, dropEnabled, dragOver, onDragOver, onDrop, emptyText, render, accent }: ColumnProps) {
  return (
    <Paper
      data-testid={`schedule-column-${riderId ?? "unassigned"}`}
      withBorder
      radius="md"
      p="xs"
      w={COLUMN_WIDTH}
      style={{
        flex: `0 0 ${COLUMN_WIDTH}px`,
        display: "flex",
        flexDirection: "column",
        background: dragOver ? "light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-5))" : "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))",
        borderColor: dragOver ? "var(--mantine-color-blue-5)" : undefined,
        borderTop: accent ? `3px solid var(--mantine-color-${accent}-6)` : undefined
      }}
      onDragOver={
        dropEnabled && riderId
          ? (event) => {
              event.preventDefault()
              onDragOver(riderId)
            }
          : undefined
      }
      onDragLeave={dropEnabled ? () => onDragOver(null) : undefined}
      onDrop={dropEnabled && riderId ? (event) => onDrop(event, riderId) : undefined}
    >
      <Stack gap={4} mb="xs">
        <Text fw={700} fz={14} lineClamp={1}>
          {title}
        </Text>
        {subtitle}
      </Stack>
      <ScrollArea.Autosize mah="calc(100vh - 330px)" offsetScrollbars>
        <Stack gap="xs">
          {stops.length === 0 ? (
            <Text fz={12} c="dimmed" ta="center" py="md">
              {emptyText}
            </Text>
          ) : (
            stops.map((stop) => <Box key={stop.id}>{render(stop)}</Box>)
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Paper>
  )
}

function RiderSubtitle({ row, country }: { row: DailyScheduleRider; country?: string }) {
  return (
    <Group gap={4} wrap="wrap">
      {country !== undefined && <CountryBadge country={country} size="xs" />}
      <Badge variant="light" color={ACTION_COLOR[STOP_ACTION.DELIVERY]} size="xs">
        {row.deliveryCount} entrega{row.deliveryCount === 1 ? "" : "s"}
      </Badge>
      <Badge variant="light" color={ACTION_COLOR[STOP_ACTION.PICKUP]} size="xs">
        {row.pickupCount} recojo{row.pickupCount === 1 ? "" : "s"}
      </Badge>
      {row.toConfirmCount > 0 && (
        <Badge variant="filled" color="pink" size="xs">
          {row.toConfirmCount} por confirmar
        </Badge>
      )}
    </Group>
  )
}

export default function DailySchedulePage() {
  const permisos = useLogisticsPermissions()
  // Asignar (arrastrar, elegir despachador, asignar en lote) y confirmar recojo son permisos distintos
  const canManage = permisos.stopAssign
  const canConfirm = permisos.stopConfirm
  const [date, setDate] = useState<Date>(new Date())
  const dateStr = dayjs(date).format("YYYY-MM-DD")
  // País (EC-15): el admin elige Todos (como siempre), Perú o Ecuador; al resto el back le da el tablero de su país
  const countryScope = useCountryScope()
  const [countrySelection, setCountrySelection] = useState<string>(ALL_COUNTRIES)
  const { data: schedule, isFetching, refetch } = useDailySchedule(dateStr, countryQueryParam(countryScope, countrySelection))
  const assignableRiders = useAssignableRiders()
  const assignStopsMutation = useAssignStops()
  const confirmarRecojoMutation = useConfirmarRecojo()

  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkRiderId, setBulkRiderId] = useState<string | null>(null)
  const [dragOverRider, setDragOverRider] = useState<string | null>(null)

  useEffect(() => {
    headerStore.setTitle("Programación diaria")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  const riderOptions = useMemo<RiderOption[]>(() => assignableRiders.map((r) => ({ id: r.id, name: r.name, country: r.country })), [assignableRiders])
  const riderCountryById = useMemo(() => new Map(riderOptions.map((r) => [r.id, r.country])), [riderOptions])
  const allStops = useMemo(() => [...(schedule?.unassigned ?? []), ...(schedule?.riders.flatMap((r) => r.stops) ?? [])], [schedule])
  const stopById = useMemo(() => new Map(allStops.map((s) => [s.id, s])), [allStops])

  const matchesFilter = (stop: DeliveryStop) => typeFilter === "all" || stop.action === typeFilter
  const unassigned = (schedule?.unassigned ?? []).filter(matchesFilter).sort(byTime)
  const toConfirmTotal = (schedule?.riders ?? []).reduce((acc, r) => acc + r.toConfirmCount, 0)
  // Asignar en lote: solo despachadores del país de los puntos elegidos (países distintos = ninguno)
  const selectedStops = [...selected].map((id) => stopById.get(id)).filter((stop): stop is DeliveryStop => !!stop)
  const bulkRiderOptions = ridersForStops(riderOptions, selectedStops)
  const bulkMixCountries = selectedStops.length > 0 && !commonCountryOf(selectedStops)
  const bulkRiderValue = bulkRiderOptions.some((r) => r.id === bulkRiderId) ? bulkRiderId : null
  // Con los dos países a la vista, cada columna muestra de qué país es su despachador
  const showRiderCountry = countryScope.canSeeAllCountries && countrySelection === ALL_COUNTRIES

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const assign = async (stopIds: string[], riderId: string) => {
    await assignStopsMutation.mutateAsync({ stopIds, riderId }).catch(() => undefined)
    setSelected(new Set())
    setBulkRiderId(null)
  }

  const confirmPickup = (stop: DeliveryStop) => {
    const riderName = riderNameOf(stop.rider) ?? "el despachador"
    openConfirmModal({
      title: "Confirmar recojo",
      centered: true,
      children: (
        <Stack gap={4}>
          <Text size="sm">
            ¿Confirmar el recojo <b>{stop.correlativo}</b>? Pasará a <b>Recojo programado</b> y se sumará a la programación de <b>{riderName}</b>.
          </Text>
          <Text size="xs" c="dimmed">
            {esPuntoEcuador(stop) ? "Se actualiza en Odoo y se refleja en la app del despachador." : "Se actualiza en Odoo, se envía a DISET y se refleja en la app del despachador."}
          </Text>
        </Stack>
      ),
      labels: { confirm: "Confirmar recojo", cancel: "Volver" },
      confirmProps: { color: "teal", leftSection: <IconCheck size={15} /> },
      onConfirm: async () => {
        await confirmarRecojoMutation.mutateAsync(stop.id).catch(() => undefined)
      }
    })
  }

  const handleDragStart = (event: DragEvent<HTMLDivElement>, stop: DeliveryStop) => {
    // Si se arrastra un punto que está seleccionado, viaja toda la selección; si no, solo ese
    const ids = selected.has(stop.id) ? [...selected] : [stop.id]
    event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(ids))
    event.dataTransfer.effectAllowed = "move"
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>, riderId: string) => {
    event.preventDefault()
    setDragOverRider(null)
    try {
      const ids = JSON.parse(event.dataTransfer.getData(DRAG_TYPE)) as string[]
      if (Array.isArray(ids) && ids.length) void assign(ids, riderId)
    } catch {
      // arrastre ajeno a este tablero: se ignora
    }
  }

  // Al cambiar de día se limpia la selección: los ids de un día no aplican al otro
  const goToDate = (next: Date) => {
    setDate(next)
    setSelected(new Set())
  }
  const changeCountry = (next: string) => {
    setCountrySelection(next)
    setSelected(new Set())
    setBulkRiderId(null)
  }
  const changeDay = (delta: number) => goToDate(dayjs(date).add(delta, "day").toDate())
  const busy = assignStopsMutation.isPending

  const renderCard = (stop: DeliveryStop, showFormerRider: boolean) => (
    <StopCard
      stop={stop}
      canManage={canManage}
      canConfirm={canConfirm}
      selected={selected.has(stop.id)}
      riders={riderOptions}
      onToggle={toggle}
      onAssign={(ids, riderId) => void assign(ids, riderId)}
      onConfirmPickup={confirmPickup}
      onDragStart={handleDragStart}
      showFormerRider={showFormerRider}
    />
  )

  return (
    <Stack gap="sm" pos="relative">
      <LoadingOverlay visible={busy} zIndex={50} overlayProps={{ blur: 1 }} />

      <Group justify="space-between" wrap="wrap">
        <Group gap="xs">
          <ActionIcon variant="default" onClick={() => changeDay(-1)} aria-label="Día anterior">
            <IconChevronLeft size={16} />
          </ActionIcon>
          <DatePickerInput
            value={date}
            onChange={(value) => {
              if (value) goToDate(dayjs(value).toDate())
            }}
            valueFormat="DD/MM/YYYY"
            leftSection={<IconCalendarEvent size={16} />}
            w={220}
            allowDeselect={false}
          />
          <ActionIcon variant="default" onClick={() => changeDay(1)} aria-label="Día siguiente">
            <IconChevronRight size={16} />
          </ActionIcon>
          <Button variant="light" size="xs" onClick={() => goToDate(new Date())}>
            Hoy
          </Button>
        </Group>

        <Group gap="xs">
          {countryScope.canSeeAllCountries && <SegmentedControl size="xs" aria-label="País" value={countrySelection} onChange={changeCountry} data={COUNTRY_SELECTION_OPTIONS} />}
          <SegmentedControl
            size="xs"
            value={typeFilter}
            onChange={(value) => setTypeFilter(value as TypeFilter)}
            data={[
              { value: "all", label: "Todo" },
              { value: STOP_ACTION.DELIVERY, label: "Entregas" },
              { value: STOP_ACTION.PICKUP, label: "Recojos" }
            ]}
          />
          <Badge variant="light" color="gray" size="lg">
            Sin asignar: {schedule?.unassigned.length ?? 0}
          </Badge>
          <Badge variant={toConfirmTotal > 0 ? "filled" : "light"} color="pink" size="lg">
            Por confirmar: {toConfirmTotal}
          </Badge>
        </Group>
      </Group>

      {canManage && selected.size > 0 && (
        <Paper withBorder radius="md" p="xs" style={{ background: "light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-6))" }}>
          <Group justify="space-between" wrap="wrap">
            <Text fz={13} fw={600}>
              {selected.size} punto{selected.size === 1 ? "" : "s"} seleccionado{selected.size === 1 ? "" : "s"}
            </Text>
            <Group gap="xs">
              <Select
                size="xs"
                w={220}
                searchable
                placeholder={bulkMixCountries ? "Elige puntos de un solo país" : "Asignar a…"}
                disabled={bulkMixCountries}
                value={bulkRiderValue}
                onChange={setBulkRiderId}
                data={bulkRiderOptions.map((r) => ({ value: r.id, label: r.name }))}
              />
              <Button size="xs" disabled={!bulkRiderValue} onClick={() => bulkRiderValue && void assign([...selected], bulkRiderValue)}>
                Asignar
              </Button>
              <ActionIcon variant="subtle" color="gray" onClick={() => setSelected(new Set())} aria-label="Limpiar selección">
                <IconX size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Paper>
      )}

      {canManage && (
        <Text fz={12} c="dimmed">
          Arrastra un punto a la columna de un despachador (o usa el botón de asignar de la tarjeta). Asignar no programa el recojo: usa "Confirmar recojo" para sumarlo a su programación.
        </Text>
      )}

      <ScrollArea type="auto" offsetScrollbars>
        <Group align="flex-start" wrap="nowrap" gap="sm" pb="xs" pos="relative" mih={200}>
          <LoadingOverlay visible={isFetching && !schedule} zIndex={10} />
          <Column
            title="Sin asignar"
            subtitle={
              <Text fz={11} c="dimmed">
                {unassigned.length} punto{unassigned.length === 1 ? "" : "s"} por asignar
              </Text>
            }
            stops={unassigned}
            dropEnabled={false}
            dragOver={false}
            onDragOver={setDragOverRider}
            onDrop={handleDrop}
            emptyText="Todo está asignado"
            render={(stop) => renderCard(stop, true)}
            accent="gray"
          />
          {(schedule?.riders ?? []).map((row) => (
            <Column
              key={row.rider.id}
              title={row.rider.name}
              subtitle={<RiderSubtitle row={row} country={showRiderCountry ? (row.rider.country ?? riderCountryById.get(row.rider.id) ?? "") : undefined} />}
              stops={row.stops.filter(matchesFilter).sort(byTime)}
              riderId={row.rider.id}
              dropEnabled={canManage}
              dragOver={dragOverRider === row.rider.id}
              onDragOver={setDragOverRider}
              onDrop={handleDrop}
              emptyText={canManage ? "Sin puntos: arrastra uno aquí" : "Sin puntos"}
              render={(stop) => renderCard(stop, false)}
              accent={row.isAvailable ? "teal" : "blue"}
            />
          ))}
        </Group>
      </ScrollArea>
    </Stack>
  )
}
